/**
 * Runtime — owns the FieldEngine and drives the session loop.
 *
 * This is the glue between the framework-agnostic orchestrator and the browser:
 * it holds the engine, the spatial backend, the mutation adapter, and the tick /
 * mutate / exchange timers, and it mirrors snapshots into the zustand store for
 * rendering. Dwell detection itself lives in the Field component's rAF loop,
 * which calls `engine.dwell` directly.
 *
 * ── Lifetime rules (audit 2026-10) ─────────────────────────────────────────
 *  - `gen` is a session generation. `teardown()`, `stopLoops()` and `finish()`
 *    bump it, and every async path re-checks it after each `await`, so a
 *    request that was in flight when the session ended can never write the old
 *    session's Watcher line / fragments into the next participant's store.
 *  - Every timer is stored and cleared. `start()` is idempotent: it tears down
 *    whatever was live first (React StrictMode runs the ?demo=1 effect twice).
 *  - Watcher and mutation passes are single-flight: a tick that finds the
 *    previous request still running is skipped, never stacked.
 *  - The Watcher uploads a frame only while the eye (WatcherEye) is mounted.
 */

import {
  FieldEngine,
  createSession,
  fragmentFromSeed,
  makeTemplateAdapter,
  makeRng,
  seedFromString,
  computeDrift,
  buildRhizome,
  isTactic,
  type Lang,
  type MutationAdapter,
  type RegimeName,
  type SeedFragment,
  type Tactic,
} from '@wintermute/orchestrator';

import { OrientationBackend } from '../spatial/OrientationBackend.js';
import { VOBackend } from '../spatial/VOBackend.js';
import { AlvaARBackend, alvaAvailable } from '../spatial/AlvaARBackend.js';
import { WebXRBackend, webxrAvailable } from '../spatial/WebXRBackend.js';
import type { CameraPose, SpatialBackend } from '../spatial/SpatialBackend.js';
import { VideoFrameProvider } from '../spatial/FrameProvider.js';
import type { DevicePose } from '../spatial/DevicePose.js';
import { telemetry } from '../spatial/telemetry.js';
import { api } from '../api/client.js';
import { HAS_API, FIELD, SPATIAL_MODE, VLM } from '../config.js';
import { useStore } from '../state/store.js';
import { captureFrame } from '../sensing/captureFrame.js';
import { emitWinterEvent } from './events.js';
import {
  planBackends,
  startSpatialBackend,
  type BackendCapabilities,
  type BackendFactories,
} from './backendChain.js';
import { validateInbox } from './inbox.js';
import { FlightGuard } from './guards.js';

/** The real backend factories and capability probes, handed to the pure chain. */
const FACTORIES: BackendFactories = {
  orientation: () => new OrientationBackend(),
  vo: () => new VOBackend(),
  alva: () => new AlvaARBackend(),
  webxr: () => new WebXRBackend(),
};
const CAPABILITIES: BackendCapabilities = {
  webxr: webxrAvailable,
  alva: alvaAvailable,
};

/**
 * Pool bound. The corpus is ~26 seeds; mutation, the inbox and the Watcher add
 * to it for the whole session, and nothing used to remove anything. Dead
 * fragments are pruned first (see FieldEngine.prunePool).
 */
const MAX_POOL = 150;
/** First Watcher look after entry, ms. */
const FIRST_WATCH_MS = 6000;

export interface StartOptions {
  seeds: SeedFragment[];
  regime: RegimeName;
  sensingConsent: boolean;
  /** Separate consent for the cloud VLM watcher (frames leave the device). */
  watcherConsent: boolean;
  demo: boolean;
  /** Fixed seed for reproducible demo runs. */
  seed?: number;
}

class Runtime {
  engine: FieldEngine | null = null;
  spatial: SpatialBackend = new VOBackend();
  private adapter: MutationAdapter = makeTemplateAdapter(makeRng(1));
  private tickTimer: number | null = null;
  private mutateTimer: number | null = null;
  private exchangeTimer: number | null = null;
  private watchTimer: number | null = null;
  private watchKickoff: number | null = null;
  private video: HTMLVideoElement | null = null;
  private watcherConsent = false;
  /** When the field opened, for the Watcher's sense of how long you have stayed. */
  private fieldEnteredAt = Date.now();

  /** Session generation. Bumped by anything that ends or restarts the session. */
  private gen = 0;
  /** The in-progress start(), so a superseding start() can wait for it to unwind. */
  private starting: Promise<void> | null = null;
  private readonly watchFlight = new FlightGuard();
  private mutateBusy = false;
  private exchangeBusy = false;
  /**
   * True while the Watcher's eye is on screen. The eye is the consent
   * disclosure, so no frame may leave the device while it is not mounted
   * (Reflection, Ending, or any future screen without it).
   */
  private eyeMounted = false;

  /** Live camera frame source, shared by the tracker and the recorder. */
  readonly frameSource = new VideoFrameProvider(null);

  /** Exposed so the debug recorder can log the IMU stream alongside frames. */
  get devicePose(): DevicePose | null {
    const b = this.spatial as unknown as { pose?: DevicePose };
    return b.pose ?? null;
  }

  /** Attach the back-camera video to the spatial backend (VO) and the watcher. */
  attachVideo(video: HTMLVideoElement | null): void {
    this.video = video;
    this.frameSource.setVideo(video);
    this.spatial.setFrameSource?.(this.frameSource);
  }

  /** WatcherEye registers here while mounted. See `eyeMounted`. */
  setWatcherEyeMounted(mounted: boolean): void {
    this.eyeMounted = mounted;
  }

  /**
   * Start a session. Idempotent: anything already running (including a start
   * still awaiting its backend) is torn down first, and the superseded start
   * stops whatever it opened and returns without touching state.
   */
  start(opts: StartOptions): Promise<void> {
    const prev = this.starting;
    this.teardown();
    const gen = this.gen;
    const run = (async () => {
      // Let a superseded start unwind (stop its backend) before we claim the
      // camera/WASM again.
      if (prev) await prev.catch(() => undefined);
      if (gen !== this.gen) return;
      await this.doStart(opts, gen);
    })();
    this.starting = run;
    const clear = () => {
      if (this.starting === run) this.starting = null;
    };
    run.then(clear, clear);
    return run;
  }

  private async doStart(opts: StartOptions, gen: number): Promise<void> {
    const seed = opts.seed ?? (opts.demo ? 42 : Math.floor(Math.random() * 1e9));
    const session = createSession(undefined, opts.sensingConsent);
    const pool = opts.seeds.map((s) => fragmentFromSeed(s, session.id));

    this.engine = new FieldEngine({ session, pool, regime: opts.regime, seed });
    this.adapter = this.buildAdapter(session.id, seed);
    this.watcherConsent = opts.watcherConsent && HAS_API;
    this.fieldEnteredAt = Date.now();

    // Pick the best backend that actually starts, before the field mounts. The
    // frame source is attached BEFORE start() so a backend that needs frames
    // during initialisation has them.
    const candidates = await planBackends(SPATIAL_MODE, CAPABILITIES, FACTORIES);
    if (gen !== this.gen) return;
    const backend = await startSpatialBackend(
      candidates,
      (b) => b.setFrameSource?.(this.frameSource),
      FACTORIES.vo,
      () => gen !== this.gen,
    );
    if (gen !== this.gen) {
      // Superseded while the backend was starting: release it, touch nothing.
      try {
        backend.stop();
      } catch {
        /* already gone */
      }
      return;
    }
    this.spatial = backend;
    this.watchBackendFailure(backend, gen);

    if (HAS_API) void api.createSession(session.id, opts.sensingConsent);

    this.pushSnapshot();
    this.startTimers();
  }

  /**
   * `confirmStarted()` only vouches for the first few seconds, and AlvaAR's real
   * `Initialize()` happens after `start()` returns. A permanent failure after
   * that (e.g. WASM out of memory on a low-end phone) used to leave the field
   * running on a backend that would never track again. Swap to VO instead.
   */
  private watchBackendFailure(backend: SpatialBackend, gen: number): void {
    const b = backend as { onFailure?(cb: (f: unknown) => void): () => void };
    b.onFailure?.(() => {
      if (gen !== this.gen || this.spatial !== backend) return;
      void this.fallBackToVO(backend, gen);
    });
  }

  private async fallBackToVO(dead: SpatialBackend, gen: number): Promise<void> {
    console.warn('[spatial] AlvaAR failed permanently; falling back to VO');
    try {
      dead.stop();
    } catch {
      /* already gone */
    }
    const vo = FACTORIES.vo();
    vo.setFrameSource?.(this.frameSource);
    try {
      await vo.start();
    } catch {
      /* VO is the floor; a failed start still leaves a usable shell */
    }
    if (gen !== this.gen || this.spatial !== dead) {
      try {
        vo.stop();
      } catch {
        /* already gone */
      }
      return;
    }
    this.spatial = vo;
    this.pushSnapshot();
  }

  private buildAdapter(sessionId: string, seed: number): MutationAdapter {
    const template = makeTemplateAdapter(makeRng(seed ^ seedFromString(sessionId)));
    if (!HAS_API) return template;
    return {
      kind: 'llm',
      async mutate(req) {
        const r = await api.rewrite({
          op: req.op,
          text: req.source.text,
          lang: req.lang ?? req.source.lang,
          tactic: req.source.tactic,
          dwellContext: req.dwellContext,
        });
        if (r && r.text) return { text: r.text, via: r.via };
        return template.mutate(req); // graceful offline fallback
      },
    };
  }

  private startTimers(): void {
    this.tickTimer = window.setInterval(() => this.onTick(), FIELD.tickMs);
    this.mutateTimer = window.setInterval(() => void this.onMutate(), FIELD.mutateMs);
    if (HAS_API) {
      this.exchangeTimer = window.setInterval(() => void this.onExchange(), 15000);
    }
    if (this.watcherConsent) {
      // First look shortly after entry, then on the configured interval. The
      // kickoff handle is kept so teardown can cancel it — it used to be
      // fire-and-forget and could land after a restart.
      this.watchKickoff = window.setTimeout(() => {
        this.watchKickoff = null;
        void this.onWatch();
      }, FIRST_WATCH_MS);
      this.watchTimer = window.setInterval(() => void this.onWatch(), VLM.intervalMs);
    }
  }

  private clearTimers(): void {
    if (this.tickTimer !== null) window.clearInterval(this.tickTimer);
    if (this.mutateTimer !== null) window.clearInterval(this.mutateTimer);
    if (this.exchangeTimer !== null) window.clearInterval(this.exchangeTimer);
    if (this.watchTimer !== null) window.clearInterval(this.watchTimer);
    if (this.watchKickoff !== null) window.clearTimeout(this.watchKickoff);
    this.tickTimer = this.mutateTimer = this.exchangeTimer = this.watchTimer = null;
    this.watchKickoff = null;
  }

  private onTick(): void {
    if (!this.engine) return;
    this.engine.tick();
    this.pushSnapshot();
  }

  private async onMutate(): Promise<void> {
    const engine = this.engine;
    const gen = this.gen;
    // Single-flight: an LLM round trip can outlast the 4.5 s interval.
    if (!engine || this.mutateBusy) return;
    this.mutateBusy = true;
    try {
      // Per-parent cap and cooldown live in the engine (see mutateEligible).
      const kids = await engine.mutateEligible(this.adapter, { max: 2 });
      if (gen !== this.gen) return; // session ended while the LLM was thinking
      if (kids.length > 0) {
        for (const k of kids) {
          emitWinterEvent({
            type: 'mutate',
            fragmentId: k.id,
            parentId: k.parents[0] ?? k.id,
            op: k.mutatedBy ?? 'mutate',
          });
        }
        const last = kids[kids.length - 1]!;
        useStore.getState().setLastMutation({ op: last.mutatedBy ?? 'mutate', text: last.text });
        // Contribute cultivated lineage upstream for others (store-and-forward).
        if (HAS_API) void api.contribute(engine.session.id, kids);
      }
      engine.prunePool(MAX_POOL);
      if (kids.length > 0) this.pushSnapshot();
    } catch {
      // An adapter failure is not fatal; the engine already charged the attempt
      // to the parent's cooldown, so this cannot spin.
    } finally {
      if (gen === this.gen) this.mutateBusy = false;
    }
  }

  /**
   * Plant a Watcher line on the object it named.
   *
   * `box` is a normalised [x0,y0,x1,y1] with y down — the model's own bounds
   * for the thing. We aim at its centre. `cell` is the coarse 3x3 fallback the
   * server falls back to when no box came back: [col,row], so the cell centre
   * is (col + 0.5) / 3. Neither is a guarantee of accuracy, but both are the
   * Watcher's own choice of WHERE, and honouring it approximately beats
   * dropping it — which is what happened until now.
   *
   * Silent when the backend cannot place at an image point (the orientation
   * shell backend has no notion of one): the fragment still exists in the
   * field and `anchor()` will give it an ordinary bearing.
   */
  private anchorObservation(
    id: string,
    box: [number, number, number, number] | null,
    cell: [number, number] | null,
    at?: CameraPose,
  ): void {
    const place = this.spatial.anchorAtImagePoint;
    if (!place) return;
    let nx: number;
    let ny: number;
    if (box) {
      nx = (box[0] + box[2]) / 2;
      ny = (box[1] + box[3]) / 2;
    } else if (cell) {
      nx = (cell[0] + 0.5) / 3;
      ny = (cell[1] + 0.5) / 3;
    } else {
      return; // the Watcher named nothing locatable; leave it to anchor().
    }
    if (!Number.isFinite(nx) || !Number.isFinite(ny)) return;
    place.call(
      this.spatial,
      id,
      Math.max(0, Math.min(1, nx)),
      Math.max(0, Math.min(1, ny)),
      at,
    );
  }

  /** Close the eye's "looking" state. Idempotent. */
  private endWatching(): void {
    useStore.getState().setWatcherLooking(false);
    emitWinterEvent({ type: 'watching', active: false });
  }

  /** The Watcher: capture a frame, get an observation, speak it as the Directive. */
  private async onWatch(): Promise<void> {
    const engine = this.engine;
    const gen = this.gen;
    if (!engine || !this.watcherConsent || !this.video) return;
    // Consent hinges on the eye being visible. No eye, no frame — whatever else
    // is true (Reflection, Ending, a screen added later).
    if (!this.eyeMounted) return;
    if (useStore.getState().phase !== 'field') return;

    // Single-flight. The interval is 22 s and a request can outlast it; the old
    // code started a second one and the first to finish closed the eye while the
    // other was still uploading.
    const leave = this.watchFlight.tryEnter();
    if (!leave) return;

    const frame = captureFrame(this.video);
    if (!frame) {
      leave();
      return;
    }

    // ⚠️ Stamp the pose NOW, with the frame. The reply lands seconds later, by
    // which time the participant has turned; a ray built from the pose at
    // REPLY time fires from where they are pointing now through an image
    // coordinate describing where they were pointing then, and lands on
    // nothing. See anchorAtImagePoint's pose-stamping note.
    const capturedAt = this.spatial.cameraPose?.() ?? undefined;

    useStore.getState().setWatcherLooking(true);
    // The eye opens as it starts looking. This is the honest disclosure, so it
    // is driven by the real request lifecycle and never simulated.
    useStore.getState().bumpWatcherBlink();
    emitWinterEvent({ type: 'watching', active: true });
    try {
      const dwellContext = engine.snapshot().dominant.slice(0, 2);
      // The soul-read. `computeDrift` has always computed this; it was only
      // ever shown at the ending, as a summary card with a percentage on it.
      // Sent live, it becomes the one thing the Watcher knows about the person
      // — see driftReading() in server/src/observe.ts and
      // docs/ART_DIRECTION_WATCHER.md.
      const drift = computeDrift(engine.session);
      const res = await api.observe({
        image: frame,
        mediaType: 'image/jpeg',
        dwellContext,
        sessionId: engine.session.id,
        tacticShare: drift.tacticShare,
        elapsedSec: (Date.now() - this.fieldEnteredAt) / 1000,
      });
      // The session may have ended or restarted during the round trip; a reply
      // for the OLD engine must not become the next participant's Watcher line.
      if (gen !== this.gen || engine !== this.engine) return;
      if (res && res.text) {
        // The reply is untrusted: a tactic outside the enum would create a NaN
        // key in tacticDwell the moment the participant dwells on the line.
        const tactic: Tactic = isTactic(res.tactic) ? res.tactic : 'doubt';
        const seed: SeedFragment = {
          text: res.text,
          lang: 'en' as Lang,
          tactic,
        };
        const frag = fragmentFromSeed(seed, engine.session.id);
        // The watcher's lines arrive already rooted — the system saw and spoke.
        frag.vitality = 0.85;
        frag.originSession = 'watcher';
        engine.injectSurfaced(frag);

        // Put the writing ON the thing it named, from the pose the frame was
        // captured at. The server has always returned `box` (and `cell` as a
        // coarse fallback) and the backend has always implemented
        // `anchorAtImagePoint`; nothing ever joined the two, so every Watcher
        // line lived only in screen space and the grounding the model was
        // asked for — at the cost of sending a full-resolution frame off the
        // device — was discarded on arrival.
        this.anchorObservation(frag.id, res.box, res.cell, capturedAt);

        useStore.getState().setLastObservation({
          text: res.text,
          tactic,
          object: res.object,
          via: res.via,
          reason: res.reason,
          register: res.register,
        });
        emitWinterEvent({ type: 'observe', fragmentId: frag.id, text: res.text });
        this.pushSnapshot();
      }
    } finally {
      // Only the last request to leave closes the eye, and only if this session
      // is still the one that opened it (teardown resets the guard and closes
      // the eye itself).
      if (leave()) this.endWatching();
    }
  }

  private async onExchange(): Promise<void> {
    const engine = this.engine;
    const gen = this.gen;
    if (!engine || !HAS_API || this.exchangeBusy) return;
    this.exchangeBusy = true;
    try {
      const inbox = await api.inbox(engine.session.id, 6);
      if (gen !== this.gen) return;
      // Fragments from other clients are untrusted. Rebuilt from validated
      // fields only; a bad tactic would poison tacticShare and the DriftMap.
      // An id already in the pool is not replaced: a foreign fragment must not
      // overwrite one the participant is looking at.
      for (const f of validateInbox(inbox)) {
        if (!engine.hasFragment(f.id)) engine.addToPool(f);
      }
      engine.prunePool(MAX_POOL);
    } finally {
      if (gen === this.gen) this.exchangeBusy = false;
    }
  }

  setRegime(r: RegimeName): void {
    this.engine?.setRegime(r);
    useStore.getState().setRegime(r);
  }

  /** Called by the field when a fragment is cultivated (held past threshold). */
  cultivate(fragmentId: string): void {
    this.engine?.cultivate(fragmentId);
    emitWinterEvent({ type: 'cultivate', fragmentId });
  }

  private pushSnapshot(): void {
    if (!this.engine) return;
    const snap = this.engine.snapshot();
    const store = useStore.getState();
    store.setLive(snap.live);
    store.setDominant(snap.dominant);
    store.setRegime(snap.regime);
  }

  /**
   * Stop everything that runs on a clock or talks to the network, and discard
   * results of requests still in flight — but keep the engine, so `finish()` and
   * the Ending's export still have the session. Bumps the generation.
   */
  stopLoops(): void {
    this.gen++;
    this.clearTimers();
    const wasWatching = this.watchFlight.reset();
    this.mutateBusy = false;
    this.exchangeBusy = false;
    // A request already uploaded cannot be recalled, but the eye must not be
    // left claiming it is still looking, nor stuck open for the next session.
    if (wasWatching || useStore.getState().watcherLooking) this.endWatching();
  }

  /** Compute and store the ending views. Also ends the session's loops. */
  finish(): void {
    this.stopLoops();
    if (!this.engine) return;
    // Report tracking quality for this run (fire and forget).
    if (HAS_API) void api.telemetry(this.engine.session.id, telemetry.summary());
    useStore.getState().setDrift(computeDrift(this.engine.session));
    const local = buildRhizome(this.engine.poolFragments());
    useStore.getState().setRhizome(local);
    // If networked, prefer the collective rhizome.
    if (HAS_API) {
      void api.rhizome().then((r) => {
        if (r && r.nodes.length) useStore.getState().setRhizome(r);
      });
    }
  }

  /**
   * Leaving the field for good (Ending): stop the loops and release the spatial
   * backend and the video, but keep the engine for export. The caller stops the
   * camera stream itself (it owns the hook).
   */
  release(): void {
    this.stopLoops();
    this.attachVideo(null);
    try {
      this.spatial.stop();
    } catch {
      /* already gone */
    }
  }

  teardown(): void {
    this.stopLoops();
    this.engine = null;
    this.watcherConsent = false;
    this.attachVideo(null);
    try {
      this.spatial.stop();
    } catch {
      /* already gone */
    }
  }
}

/** Singleton runtime for the app. */
export const runtime = new Runtime();
